import React, { useCallback, useMemo } from 'react';
import {
  Box,
  FormControl,
  ListItemText,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage, useIntl } from 'react-intl';
import { DateTime } from 'luxon';
import { Skeleton } from '@mui/material';
import { DateRange } from 'types/date-range';
import { useMeContext } from 'modules/me';

type TimesheetPeriodsType = {
  current: DateRange | null;
  last: DateRange | null;
  loading: boolean;
};

export type TimesheetPeriodSelectorProps = TimesheetPeriodsType & {
  periodType: TimesheetPeriodType;
  onSelect: (periodType: TimesheetPeriodType, range?: DateRange) => void;
};

const useStyles = makeStyles((theme) => ({
  root: {
    margin: theme.spacing(0),
    minWidth: 120,
    '& .MuiListItemText-root': {
      display: 'none'
    },
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  },
  dateRange: {
    padding: theme.spacing(0, 0.5)
  },
  hidden: {
    display: 'none'
  },
  show: {
    display: 'block'
  },
  label: {
    marginRight: theme.spacing(2),
    color: theme.palette.text.secondary
  }
}));

const useSelectStyles = makeStyles((theme) => ({
  select: {
    padding: theme.spacing(1)
  }
}));

export enum TimesheetPeriodType {
  Custom,
  Today,
  Current,
  Last
}

export const TimesheetPeriodSelector: React.FC<TimesheetPeriodSelectorProps> = ({
  periodType: period,
  onSelect,
  current,
  last,
  loading
}: TimesheetPeriodSelectorProps) => {
  const classes = useStyles();
  const selectStyles = useSelectStyles();
  const { formatMessage } = useIntl();

  const me = useMeContext();
  const format =
    me?.locale?.dateTimeSettings?.shortDateFormat?.luxonPattern ||
    'MMM d, yyyy';

  const periods = useMemo(
    () =>
      loading
        ? []
        : [
            {
              value: TimesheetPeriodType.Today,
              range: { start: DateTime.local(), end: DateTime.local() },
              text: formatMessage({ id: 'timesheetPeriods.today' })
            },
            ...(current
              ? [
                  {
                    value: TimesheetPeriodType.Current,
                    range: current,
                    text: formatMessage({
                      id: 'timesheetPeriods.currentPeriod'
                    }),
                    dateString: `(${current!.start!.toFormat(
                      format
                    )}, ${current!.end!.toFormat(format)})`
                  }
                ]
              : []),

            ...(last
              ? [
                  {
                    value: TimesheetPeriodType.Last,
                    range: last,
                    text: formatMessage({ id: 'timesheetPeriods.lastPeriod' }),
                    dateString: `(${last!.start!.toFormat(
                      format
                    )}, ${last!.end!.toFormat(format)})`
                  }
                ]
              : [])
          ],
    [format, formatMessage, current, last, loading]
  );

  const handleChange = useCallback(
    (event: any) => {
      const periodType = event.target.value;
      onSelect(
        event.target.value,
        periods.find((p) => p.value == periodType)?.range
      );
    },
    [onSelect, periods]
  );

  if (loading) {
    return <Skeleton width={80} height={45} />;
  }

  const showCustomOption =
    period === TimesheetPeriodType.Custom ? classes.show : classes.hidden;
  return (
    <Box
      component="span"
      className={classes.root}
      data-testid="timesheet-period-selector"
    >
      <FormControl variant="outlined" className={classes.root}>
        <Typography data-testid="view-label" className={classes.label}>
          <FormattedMessage id="timesheetPeriods.label" />
        </Typography>
        <Select
          classes={selectStyles}
          data-testid="timesheet-period-simple-select"
          value={period}
          onChange={handleChange}
        >
          <MenuItem
            value={TimesheetPeriodType.Custom}
            className={showCustomOption}
          >
            <FormattedMessage id="timesheetPeriods.custom" />
          </MenuItem>
          {periods.map(({ value, text, dateString }) => {
            return (
              <MenuItem key={value} value={value}>
                {text}
                {dateString && (
                  <ListItemText
                    className={classes.dateRange}
                    primary={dateString}
                  />
                )}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Box>
  );
};
